import { forwardRef } from 'react';
import type { ElementType, ReactNode, Ref } from 'react';
import pick from 'lodash/pick';
import Form from 'react-bootstrap/Form';
import InputGroup from 'react-bootstrap/InputGroup';
import { Button } from 'modules/core/components/buttons';
import { SimpleCheckboxInput } from '../../components/forms';

import { inputPropTypes } from './utils';

/**
 * Пропсы «глупого» текстового инпута (без Formik). Formik-обёртка — StringInput.
 *
 * Обработчики намеренно мягкие `(event: any) => void`: десятки разнородных потребителей
 * и lenient tsconfig (`noImplicitAny: off`) — ужесточение отдельная задача (по образцу
 * обоснования в select.types.ts).
 *
 * Сквозные пропсы (name, maxLength, disabled, onChange, onInput, onKeyDown/Up/Press,
 * onFocus, onBlur, onValidateFail) НЕ деструктурируются — остаются в `rest` и проходят
 * в Form.Control через `pick(rest, Object.keys(inputPropTypes))`. Это runtime-фильтр,
 * гарантия поведения 1:1 относительно .jsx-версии: «мусорные» пропсы .jsx-потребителей
 * (size, maxlen, autoFocus и т.п.) не доходят до DOM — замена на `...rest` сломала бы
 * это (пропсы начали бы применяться/давать React-warning'и).
 */
export interface SimpleStringInputProps {
  // идентификация/форма
  name?: string;
  controlId?: string;
  groupId?: string;
  /** Ref на корневой Form.Group (div). Реальных передач сейчас нет — тип по факту DOM-узла. */
  groupRef?: Ref<HTMLDivElement>;
  // тип/вид инпута
  as?: ElementType;
  type?: string;
  rows?: number | string;
  autoComplete?: string;
  // значение и подпись
  value?: string | number | null;
  label?: ReactNode;
  placeholder?: string;
  // состояния
  required?: boolean;
  error?: string | null;
  readOnly?: boolean;
  disabled?: boolean;
  maxLength?: string | number;
  // дизайн
  className?: string;
  rootClassName?: string;
  rowDesign?: boolean;
  isNextField?: boolean;
  // кнопка-дополнение
  showButton?: boolean;
  buttonIcon?: string;
  icon?: string;
  onClickButton?: (...args: unknown[]) => unknown;
  // контент внутри Form.Control
  inputContent?: ReactNode | ReactNode[] | ((...args: unknown[]) => ReactNode);
  children?: ReactNode;
  // чекбокс в label
  onCheckBoxLabelClick?: (...args: unknown[]) => unknown;
  onCheckBoxLabelValue?: boolean;
  /**
   * Принимается ради существующих прямых вызовов (напр. SchedulerDateTimeSelector),
   * но здесь игнорируется — это пропс Formik-обёртки StringInput, и до Form.Control
   * он не доходит (фильтруется pick). Оставлен в типе, чтобы не править вызовы.
   */
  shouldComponentUpdate?: (next: any, prev: any) => boolean;
  // сквозные в Form.Control — остаются в rest и проходят через pick(...)
  onChange?: (event: any) => void;
  onInput?: (event: any) => void;
  onKeyDown?: (event: any) => void;
  onKeyUp?: (event: any) => void;
  onKeyPress?: (event: any) => void;
  onFocus?: (event: any) => void;
  onBlur?: (event: any) => void;
  onValidateFail?: (...args: unknown[]) => unknown;
}

const SimpleStringInput = forwardRef<HTMLElement, SimpleStringInputProps>((props, ref) => {
  const {
    as,
    type = 'text',
    value,
    label,
    placeholder,
    autoComplete,
    required,
    error,
    inputContent,
    controlId,
    groupId,
    groupRef,
    className,
    children,
    rootClassName,
    showButton = false,
    buttonIcon,
    onClickButton,
    readOnly = false,
    rows,
    rowDesign = false,
    isNextField,
    onCheckBoxLabelClick,
    onCheckBoxLabelValue,
    ...rest
  } = props;

  const input = (
    <Form.Control
      { ...pick(rest, Object.keys(inputPropTypes)) }
      ref={ ref }
      as={ as }
      type={ type }
      value={ value }
      placeholder={ placeholder }
      autoComplete={ autoComplete }
      required={ required }
      className={ className }
      readOnly={ readOnly }
      rows={ rows }
    >
      { (typeof inputContent === 'function') ? inputContent() : inputContent }
    </Form.Control>
  );

  return (
    <Form.Group id={ groupId } ref={ groupRef } controlId={ controlId } className={ `${ rootClassName ?? '' }${ rowDesign ? ' row-design' : '' }${ error ? ' field-has-error' : '' }${ isNextField && !error && !rest.disabled ? ' field-is-next' : '' }` }>

      { label && !!onCheckBoxLabelClick && (
        <Form.Label className={ `label-with-checkbox ${ required ? 'required' : '' }` }>
          <div>
            { label }
          </div>
          <div className='check-box-in-label'>
            <SimpleCheckboxInput
              name={ props.name+'_check' }
              checked={ onCheckBoxLabelValue }
              onChange={ onCheckBoxLabelClick }
            />
          </div>
        </Form.Label>
      ) }

      { label && !onCheckBoxLabelClick && (
        <Form.Label className={ required ? 'required' : null }>
          { label }
        </Form.Label>
      ) }

      { showButton
        && (
          <div className='input-group'>
            { children ? (
              <InputGroup style={ !children ? { display: 'inline' } : {} }>
                { input }
                { children }
              </InputGroup>
            ) : input }
            <div className='input-group-append'>
              <Button
                variant='primary'
                text=''
                icon={ buttonIcon || 'arrow-down' }
                onClick={ onClickButton }
                disabled={ readOnly }
              />
            </div>
          </div>
        ) }

      { !showButton
        && (
          children ? (
            <InputGroup style={ !children ? { display: 'inline' } : {} }>
              { input }
              { children }
            </InputGroup>
          ) : input
        ) }

      { error && (type !== 'hidden') && (
        <small className='form-text text-danger'>
          { error }
        </small>
      ) }
    </Form.Group>
  );
});

SimpleStringInput.displayName = 'SimpleStringInput';

export default SimpleStringInput;
